import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useTileSize} from "./use-tile-size.ts";
import {useMapBoundingBox} from "../contexts/MapBoundingBox";

export type BottomSheetState = 'collapsed' | 'half' | 'expanded';

export const _STATE_HEIGHTS: Record<BottomSheetState, number> = {
    collapsed: 0.20,  // 20% of viewport
    half: 0.40,       // 50% of viewport
    expanded: 0.70,   // 85% of viewport
};

export interface DragState {
    startY: number;
    currentY: number;
    startTime: number;
    lastY: number;
    lastTime: number;
}

export interface UseBottomSheetOptions {
    initialState?: BottomSheetState;
    persist?: boolean;
    storageKey?: string;
    /** Extra pixels added to the visible peek of the `half` state. Use this when
     *  content (e.g. a banner) is rendered above the events carousel so the
     *  carousel doesn't get pushed out of view. */
    extraHalfPx?: number;
}

export type PointerLikeEvent =
    | React.TouchEvent
    | React.MouseEvent
    | React.PointerEvent;

export interface UseBottomSheetReturn {
    state: BottomSheetState;
    isDragging: boolean;
    translateY: number;
    handleTouchStart: (e: PointerLikeEvent) => void;
    handleTouchMove: (e: PointerLikeEvent) => void;
    handleTouchEnd: () => void;
    handleTouchCancel: () => void;
    /**
     * Begin a drag at a specific Y coordinate. Used when a drag is engaged
     * retroactively (e.g. after the user has already moved past a threshold
     * starting from somewhere other than the handle), so the dragStateRef's
     * startY matches the original pointerdown rather than where engagement
     * was detected. Without this the sheet would visually snap by the
     * accumulated delta the moment we engage.
     */
    beginDragAt: (clientY: number) => void;
    setState: (state: BottomSheetState) => void;
    cycleState: (state?: BottomSheetState) => void;
    wasMoved: () => boolean;
    bottomSheetHeight: number;
    disallowExpanded: boolean;
    setDisallowExpanded: (disallow: boolean) => void;
}
export interface BottomSheetContextValue {
    state: BottomSheetState;
    setState: (state: BottomSheetState) => void;
    /** When true, the `expanded` snap point is removed from cycling, dragging,
     *  and direct state changes; useful when the sheet body has nothing
     *  meaningful to show at full height (e.g. empty state). */
    disallowExpanded: boolean;
    setDisallowExpanded: (disallow: boolean) => void;
}

export const BottomSheetContext = React.createContext<BottomSheetContextValue>({
    state: 'half',
    setState: () => void 0,
    disallowExpanded: false,
    setDisallowExpanded: () => void 0,
});

const VELOCITY_THRESHOLD = 0.5; // px/ms - fast swipe threshold
const SNAP_THRESHOLD = 0.3; // 30% drag distance threshold
const TAP_THRESHOLD_PX = 6; // movement under this is a tap, not a drag

/**
 * Hook to manage bottom sheet state, gestures, and animations
 * Supports touch and mouse events with velocity-based snapping
 */
/**
 * Calculate bottom sheet state heights based on map container size
 * - Collapsed: Only handle visible
 * - Half: 1 row of tiles visible
 * - Expanded: Full bottom sheet (3 rows of tiles)
 */
export function useStateHeights(extraHalfPx: number = 0) {
    const tileSize = useTileSize();
    const mapBoundingBox = useMapBoundingBox();

    return useMemo(() => {
        // Fallback to window height if map hasn't been measured yet
        const mapHeight = mapBoundingBox?.height || window.innerHeight;

        // Visible peek-height of the handle when the sheet is collapsed.
        // Sized so the magenta bar + chevron affordance are fully shown,
        // matching the handle's pt-4 + bar(5) + gap-1.5(6) + chevron(13) layout
        // with a few px of breathing room below.
        const HANDLE_HEIGHT = 46;

        // Collapsed: Only handle visible (everything else hidden)
        const collapsed = mapHeight - 64 - HANDLE_HEIGHT;

        // Half: 1 row of tiles visible (tileSize + carousel padding) plus any
        // extra peek requested by the host (e.g. mobile banner above the list).
        // The header was removed on mobile, so the peek only needs to clear the
        // handle + the carousel's own padding — a smaller reserve reveals more map.
        const half = mapHeight - HANDLE_HEIGHT - (tileSize + 80 + extraHalfPx);

        // Expanded: 3 rows of tiles visible
        const expanded = mapHeight - (3 * tileSize + 170);
        return {
            collapsed: collapsed / mapHeight,
            half: half / mapHeight,
            expanded: 0.05,
        };
    }, [mapBoundingBox?.height, tileSize, extraHalfPx]);
}
const useMapBoxBottomRightControlPosition = (traslateY: number) => {
    const mapBoundingBox = useMapBoundingBox();
    const mapHeight = mapBoundingBox?.height || window.innerHeight;
    const controlGroup = document.querySelector<HTMLDivElement>('.mapboxgl-ctrl-bottom-right')
    if (controlGroup) {
        const styles = {
            transform: `translateY(${traslateY - mapHeight}px)`,
        };
        Object.assign(controlGroup.style, styles);
    }
}
export function useBottomSheet(options: UseBottomSheetOptions = {}): UseBottomSheetReturn {
    const {
        initialState = 'collapsed',
        persist = false,
        storageKey = 'bottomSheetState',
        extraHalfPx = 0,
    } = options;
    const STATE_HEIGHTS = useStateHeights(extraHalfPx);
    const mapBoundingBox = useMapBoundingBox();
    const mapHeight = mapBoundingBox?.height || window.innerHeight;
    // Load initial state from localStorage if persistence enabled
    const getInitialState = (): BottomSheetState => {
        if (persist && typeof window !== 'undefined') {
            const saved = localStorage.getItem(storageKey);
            if (saved && (saved === 'collapsed' || saved === 'half' || saved === 'expanded')) {
                return saved as BottomSheetState;
            }
        }
        return initialState;
    };

    const [state, setStateRaw] = useState<BottomSheetState>(getInitialState);
    const [isDragging, setIsDragging] = useState(false);
    const [dragOffset, setDragOffset] = useState(0);
    const [disallowExpanded, setDisallowExpanded] = useState(false);
    const dragStateRef = useRef<DragState | null>(null);

    // Wrap setState so external callers cannot force the sheet into a state
    // that's currently disallowed (e.g. `expanded` during empty results).
    const setState = useCallback((next: BottomSheetState) => {
        if (next === 'expanded' && disallowExpanded) {
            setStateRaw('half');
            return;
        }
        setStateRaw(next);
    }, [disallowExpanded]);

    // If the disallow flag flips on while the sheet is already expanded, snap
    // back to `half` so the empty-state view isn't stuck at full height.
    useEffect(() => {
        if (disallowExpanded && state === 'expanded') {
            setStateRaw('half');
        }
    }, [disallowExpanded, state]);
    // Tracks whether the current pointer interaction moved past TAP_THRESHOLD_PX.
    // Used so the consumer can distinguish a tap from a drag without depending on
    // the (potentially stale) `isDragging` state during synthetic click events.
    const movedRef = useRef(false);
    // Persist state to localStorage
    useEffect(() => {
        if (persist && typeof window !== 'undefined') {
            localStorage.setItem(storageKey, state);
        }
    }, [state, persist, storageKey]);

    // Calculate translateY based on state and drag offset
    console.log("--------------------", state, STATE_HEIGHTS[state])
    const translateY = isDragging
        ? dragOffset
        : (STATE_HEIGHTS[state] * mapHeight) ;
    console.log("--------------------", translateY)
    useMapBoxBottomRightControlPosition(translateY + 64)
    const getClientY = (e: PointerLikeEvent): number => {
        if ('touches' in e && e.touches.length > 0) {
            return e.touches[0].clientY;
        }
        return (e as React.MouseEvent | React.PointerEvent).clientY;
    };

    const calculateSnapState = useCallback((dragState: DragState, currentState: BottomSheetState): BottomSheetState => {
        const deltaY = dragState.currentY - dragState.startY;
        const deltaTime = dragState.lastTime - dragState.startTime;
        const velocity = deltaTime > 0 ? (dragState.lastY - dragState.startY) / deltaTime : 0;

        const dragPercent = Math.abs(deltaY) / mapHeight;
        const topState: BottomSheetState = disallowExpanded ? 'half' : 'expanded';

        // Fast swipe - skip intermediate state
        if (Math.abs(velocity) > VELOCITY_THRESHOLD) {
            if (velocity > 0) {
                // Swiping down (closing)
                if (currentState === 'expanded') return 'collapsed';
                if (currentState === 'half') return 'collapsed';
                return currentState;
            } else {
                // Swiping up (opening)
                if (currentState === 'collapsed') return topState;
                if (currentState === 'half') return topState;
                return currentState;
            }
        }

        // Slow drag - snap based on threshold
        if (dragPercent > SNAP_THRESHOLD) {
            if (deltaY > 0) {
                // Dragging down
                if (currentState === 'expanded') return 'half';
                if (currentState === 'half') return 'collapsed';
                return currentState;
            } else {
                // Dragging up
                if (currentState === 'collapsed') return 'half';
                if (currentState === 'half') return topState;
                return currentState;
            }
        }

        // Not enough movement - return to current state
        return currentState;
    }, [mapHeight, disallowExpanded]);

    const beginDragAt = useCallback((clientY: number) => {
        const now = Date.now();

        dragStateRef.current = {
            startY: clientY,
            currentY: clientY,
            startTime: now,
            lastY: clientY,
            lastTime: now,
        };
        movedRef.current = false;

        setIsDragging(true);
        setDragOffset(STATE_HEIGHTS[state] * mapHeight);
    }, [state, STATE_HEIGHTS, mapHeight]);

    const handleTouchStart = useCallback((e: PointerLikeEvent) => {
        beginDragAt(getClientY(e));
    }, [beginDragAt]);

    const handleTouchMove = useCallback((e: PointerLikeEvent) => {
        if (!dragStateRef.current) return;

        const clientY = getClientY(e);
        const now = Date.now();

        // Update drag state
        const deltaY = clientY - dragStateRef.current.startY;
        if (Math.abs(deltaY) > TAP_THRESHOLD_PX) {
            movedRef.current = true;
        }
        const currentOffset = (STATE_HEIGHTS[state] * mapHeight) + deltaY; // Positive = down/hidden

        // Constrain within bounds (0 = fully visible, mapHeight = fully hidden)
        const minOffset = 0; // Fully expanded (visible)
        const maxOffset = mapHeight; // Fully collapsed (hidden)
        const constrainedOffset = Math.max(minOffset, Math.min(maxOffset, currentOffset));

        dragStateRef.current.currentY = clientY;
        dragStateRef.current.lastY = clientY;
        dragStateRef.current.lastTime = now;

        setDragOffset(constrainedOffset);
    }, [state, STATE_HEIGHTS, mapHeight]);

    const handleTouchEnd = useCallback(() => {
        if (!dragStateRef.current) {
            // Pointer was already cleared (e.g. touchcancel) — make sure we don't
            // leave isDragging stuck on, otherwise taps would never cycle again.
            setIsDragging(false);
            return;
        }

        const newState = calculateSnapState(dragStateRef.current, state);

        setIsDragging(false);
        setState(newState);
        dragStateRef.current = null;
    }, [state, calculateSnapState]);

    const handleTouchCancel = useCallback(() => {
        // System-level cancel (Android edge swipe, iOS bottom-bar gesture, etc).
        // End the drag without snapping so a subsequent tap isn't blocked by a
        // stale isDragging flag.
        setIsDragging(false);
        dragStateRef.current = null;
        movedRef.current = false;
    }, []);

    const wasMoved = useCallback(() => movedRef.current, []);

    const cycleState = useCallback((sheetState?: BottomSheetState) => {
        if(sheetState) {
            setState(sheetState)
            return
        }
        const stateOrder: BottomSheetState[] = disallowExpanded
            ? ['collapsed', 'half']
            : ['collapsed', 'half', 'expanded'];
        const currentIndex = stateOrder.indexOf(state);
        const nextIndex = (currentIndex + 1) % stateOrder.length;
        setState(stateOrder[nextIndex]);
    }, [state, setState, disallowExpanded]);

    return {
        state,
        isDragging,
        translateY,
        handleTouchStart,
        handleTouchMove,
        handleTouchEnd,
        handleTouchCancel,
        beginDragAt,
        setState,
        cycleState,
        wasMoved,
        bottomSheetHeight: mapHeight - 64,
        disallowExpanded,
        setDisallowExpanded,
    };
}

export const useBottomSheetContext = () => {
    try {
        return React.useContext(BottomSheetContext);
    } catch (error) {
        return undefined;
    }
}